import { useCallback } from 'react';
import { createPortal } from 'react-dom';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Spinner } from 'design-system';
import { useNavigate } from 'react-router';

import { Z_INDEX } from '@/config/z-index';
// eslint-disable-next-line sonarjs/todo-tag -- intentional migration marker
// TODO: replace all Dashboard internal imports below with '@/features/dashboard' once Dashboard
// feature has a public index.ts (GO-2318 tracks this; Dashboard not yet refactored).
import {
  DashboardTargetMenu,
  getUniqueDashboardName,
  getUniqueTabName,
  TAB_DEFAULT_BASE_NAME,
  useDashboard
} from '@/features/dashboard';
import type { DashboardTab, TabInfo } from '@/features/dashboard';
import { generateUniqueUUID } from '@/shared/lib/format/generateUniqueUUID';
import { useAppDispatch } from '@/store/hooks';
import { createDashboardTabMutationOptions } from '../../api/symbol-selector.options';
import {
  clearPendingAddAppDrawer,
  requestAddAppDrawer,
  SELECTOR_MODES
} from '../../state/symbolSelector.slice';

interface CreateAppMenuProps {
  /** Selector container element — used as Popper anchor (bottom-end placement) */
  containerEl?: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  selectorMode: SELECTOR_MODES;
}

const MENU_BG_COLOR_SPOT = 'var(--gx-highlight)';
const MENU_BG_COLOR_CURVE = 'var(--gx-selector-forward-curve)';

const createEmptyTab = () => ({
  id: generateUniqueUUID(new Set()),
  content: []
});

export const CreateAppMenu = ({ containerEl, open, onClose, selectorMode }: CreateAppMenuProps) => {
  const { data: allDashboards } = useDashboard();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();

  const navigateAndOpenDrawer = useCallback(
    (dashboardName: string, tabName: string) => {
      dispatch(clearPendingAddAppDrawer());
      void navigate(
        `/dashboard/${encodeURIComponent(dashboardName)}/${encodeURIComponent(tabName)}`
      );
      dispatch(requestAddAppDrawer());
    },
    [dispatch, navigate]
  );

  const rollbackNavigation = useCallback(() => {
    dispatch(clearPendingAddAppDrawer());
    void navigate(-1);
  }, [dispatch, navigate]);

  const { mutate: createTab, isPending } = useMutation(
    createDashboardTabMutationOptions(queryClient, navigateAndOpenDrawer, rollbackNavigation)
  );

  const handleSelectTab = useCallback(
    (dashboardName: string, tab: TabInfo) => {
      if (isPending) return;
      navigateAndOpenDrawer(dashboardName, tab.tabName);
      onClose();
    },
    [isPending, navigateAndOpenDrawer, onClose]
  );

  const handleCreateNewTab = useCallback(
    (dashboardName: string) => {
      if (isPending || !allDashboards) return;
      const existingTabs = (allDashboards[dashboardName] as DashboardTab | undefined) ?? {};
      const existingTabNames = Object.keys(existingTabs);
      const newTabName = getUniqueTabName(existingTabNames);
      const payload: DashboardTab = { ...existingTabs, [newTabName]: createEmptyTab() };
      createTab({ dashboardName, tabName: newTabName, payload });
      onClose();
    },
    [isPending, allDashboards, createTab, onClose]
  );

  const handleCreateNewDashboard = useCallback(() => {
    if (isPending) return;
    const existingNames = Object.keys(allDashboards || {});
    const newName = getUniqueDashboardName(existingNames);
    const payload: DashboardTab = { [TAB_DEFAULT_BASE_NAME]: createEmptyTab() };
    createTab({ dashboardName: newName, tabName: TAB_DEFAULT_BASE_NAME, payload });
    onClose();
    // Navigation is handled in the mutation's onSuccess callback;
    // returning undefined signals DashboardTargetMenu to take no further action.
  }, [isPending, allDashboards, createTab, onClose]);

  const getDashboardTabs = useCallback(
    (dashboardName: string) => {
      const tabs = allDashboards?.[dashboardName];
      if (!tabs) return null;
      return Object.keys(tabs).map(name => ({ tabId: name, tabName: name }));
    },
    [allDashboards]
  );

  const paperBgColor =
    selectorMode === SELECTOR_MODES.FORWARD_CURVE ? MENU_BG_COLOR_CURVE : MENU_BG_COLOR_SPOT;

  return (
    <>
      {isPending &&
        createPortal(
          <div
            className='fixed inset-0 flex items-center justify-center bg-black/50'
            style={{ zIndex: Z_INDEX.loadingBackdrop }}
          >
            <Spinner />
          </div>,
          document.body
        )}
      <DashboardTargetMenu
        anchorEl={containerEl ?? null}
        dashboards={Object.keys(allDashboards || {})}
        disabled={isPending}
        getTabs={getDashboardTabs}
        headerText='Select destination dashboard'
        offset={[0, 0]}
        open={open}
        paperStyle={{
          backgroundColor: paperBgColor,
          width: 182,
          minWidth: 0
        }}
        placement='bottom-end'
        zIndexWhenBelow={Z_INDEX.priorityTop}
        zIndexWhenAbove={Z_INDEX.priorityLow}
        onClose={onClose}
        onCreateDashboard={handleCreateNewDashboard}
        onCreateNewTab={handleCreateNewTab}
        onSelectTab={(dashboardName, tab) => {
          handleSelectTab(dashboardName, tab);
        }}
      />
    </>
  );
};
